import { autoinject } from 'aurelia-framework';
import { createEntityManagerAsync, getEntityManager } from './entity-manager-factory';
import { IGetDataParams, IApiResponse, ILogEngagementParams, IEngagement } from '@shared/interfaces/interfaces';
import { IdentityHelpers } from '@identity/helpers';
import * as uuid from 'uuid';
import { json } from 'aurelia-fetch-client';
import { SentryValueConverter } from '@shared/value-converters/sentry';

@autoinject
export class Repository {

  private data: any[];
  private dataParams: IGetDataParams | null = null;
  private identity!: IdentityHelpers;
  private sentry: SentryValueConverter;
  public breezeManager: breeze.EntityManager;
  readonly engagementAddress: string = '/api/content/logengagement';
  constructor(identity: IdentityHelpers, sentry: SentryValueConverter) {
    this.data = [];
    this.identity = identity;
    this.breezeManager = getEntityManager();
    this.sentry = sentry;
  }

  async getTrackers(personId: string, context? : string): Promise<breeze.Entity[]> {

    const getDataParams: IGetDataParams = {
      apiMethod: 'EngagementTrackers',
      forceRemote: false,
      liveApiMethod: 'PersonTrackers',
      filterName: 'personId',
      filterValue: personId,
      checkFetched: false,
    };

    let failed: boolean = false;
    let error: string = '';

    const response = await this.getData(getDataParams)
      .catch((err) => {
        // TODO - mirror approach to repo error in basket.ts once tested
        console.log(`Error - status = ${err.status}`);
        // this.loadError = `Error retrieving data: ${err.status}`;
        failed = true;
        error = err;
        const reportError = this.sentry.toView(
          `Repository error (getTrackers) for personId: ${personId}. ${context ? `. Context: ${context}` : ''}`,
          err);
      });

    if (failed || !response) {
      return Promise.reject(error);
    }

    return Promise.resolve(<breeze.Entity[]>response);

  }

  async logEngagement(params: ILogEngagementParams, context?: string): Promise<IApiResponse> {

    // if (!this.identity.antiForgeryDone || this.identity.forceAntiForgeryUpdate) {
      // this creates headers for http fetch client and breeze adapter
    await this.identity.initialiseIdentityAsync();
    // }

    const apiResponse = <IApiResponse>{};

    const { engagementType, engagementContentId,
      engagementGroupId, engagementResourceId, videoTrackerId,
    resourceGroupVideoId, resourceGroupMaterialId, resourceGroupWebinarId, completed } = params;

    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const engagementModel: any = {
          videoTrackerId,
          resourceGroupVideoId,
          resourceGroupMaterialId,
          resourceGroupWebinarId,
          completed,
          contentId: engagementContentId,
          resourceGroupId: engagementGroupId,
          resourceId: engagementResourceId,
          requestType: engagementType,
        };

        const response = await this.identity.httpClient.fetch(
          `${this.engagementAddress}/logEngagement?t=${Date.now()}`, {
            method: 'POST',
            body: json(engagementModel),
          });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        const reportData = this.sentry.toView(
          `Repository (logEngagement) for params: ${json(params)}`);

        if (apiResponse.statusCode === 200) {
          apiResponse.message = result.Token;
        } else {
          apiResponse.message = `Failed to log engagement: ${result.message}`;
          const reportError = this.sentry.toView(
            'Repository error (logEngagement)',
            `Status code: ${apiResponse.statusCode}. Message: ${result.message}. ${context ? `. Context: ${context}` : ''}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        apiResponse.message = 'Failed to log engagement: server API error. ' +
          'Please try again later.';

        const reportError = this.sentry.toView(
          'Repository error (logEngagement)',
          err);

        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async getData(params: IGetDataParams, context?: string): Promise<breeze.Entity[]> {

    const { apiMethod, filterName, filterValue, forceRemote,
      checkFetched, liveApiMethod, notComplete } = params;

    // var promise: Promise<Array<any>> = new Promise((resolve, reject) => {
    // resolve(this.test);
    // });
    // return promise;

    const mgr: breeze.EntityManager = getEntityManager();

    let query = breeze.EntityQuery
      .from(apiMethod);

    if (filterName.length) {
      const filter: breeze.Predicate = breeze.Predicate.create(filterName, '==', filterValue);
      query = query.where(filter);
    }

    // first see if we already have data in cache
    if (!forceRemote) {
      const cachedEntities: breeze.Entity[] = mgr.executeQueryLocally(query);

      // console.log(cachedEntities);

      if (cachedEntities.length > 0) {

        if (!checkFetched) {
          return Promise.resolve(cachedEntities);
        }

        // sometimes we need to make sure that the full entity is returned
        // so we set fetched to true when that entity is retrieved from db
        // and then check that value here, e.g ResourceGroups only query
        // does not retrieve resource elements to reduce payload until needed
        const e: any = cachedEntities[0];
        console.log(`Fetched ${filterName}: ${e.fetched}`);
        if (e.fetched) {
          return Promise.resolve(cachedEntities);
        }
      }
    }

    // fetch data from remote database if not already in cache - this may have
    // a different method name to the entity name, eg LibraryResources
    query = breeze.EntityQuery
      .from((liveApiMethod ? liveApiMethod : apiMethod) +
      (filterName ? `/${filterName}/${filterValue}/ts/${Date.now()}` : `/ts/${Date.now()}`));

    let failed: boolean = false;
    let error: string = '';
    let originalError: any;

    const queryResults = await mgr.executeQuery(query)
      .catch((err) => {
        originalError = err;
        const queryString: string = json(query.toJSON());
        failed = true;
        error = err && err.message ? err.message : 'No error message. ';
        error += err && err.details ? err.details : 'No error details. ';
        error += err && err.httpResponse ? `HTTP Response: ${err.httpResponse.status}` : 'No HTTP Response';

        const reportData = this.sentry.toView(
          `Repository (getData) for query: ${queryString}. ${context ? `. Context: ${context}` : ''}`);

        const reportStatus = this.sentry.toView(
          `Repository (getData) user status: IsAuthenticated = ${this.identity.user.IsAuthenticated}.
 UserName = ${this.identity.user.UserName}. PersonId = ${this.identity.user.PersonId}.
 IsActiveCommunity = ${this.identity.user.IsActiveCommunity}`);

        const reportError = this.sentry.toView(
          'Repository error (getData)',
          error);

        let reportErrorRaw = this.sentry.toView(
          `Repository raw error (getData): ${originalError}`);

        reportErrorRaw = this.sentry.toView(
          'Repository raw error (getData)', originalError);

      });

    if (failed || ! queryResults) {
      return Promise.reject(originalError);
    }

    if (!notComplete) {
      for (const entity of queryResults.results) {
        const e: any = entity;
        e.fetched = true;
      }
    }

    return Promise.resolve(queryResults.results);

  }

  buildEntityErrorString(entityErrors: any) {
    let getError: any;
    let entityName: string = '';
    let flattenedEntity: string = '';
    let errorString: string = '';

    for (const entityError of entityErrors) {
      getError = entityError.entity.entityAspect.getValidationErrors();
      entityName = entityError.entity.entityAspect.entityGroup.entityType.shortName;
      flattenedEntity = this.flattenEntity(entityName, entityError.entity);
      errorString += `\n${entityError.errorMessage}: ${json(flattenedEntity)}`;
    }
    return errorString;
  }

  async saveAnon(entities: breeze.Entity[] | undefined, context?: string):
    Promise<breeze.SaveResult> {

    // set up anti-forgery token if not already done
    // if (!this.identity.antiForgeryDone || this.identity.forceAntiForgeryUpdate) {
      // this creates headers for http fetch client and breeze adapter
    await this.identity.initialiseIdentityAsync();
    // }

    const adapter: any = breeze.config.getAdapterInstance('ajax');

    const saveOptions: breeze.SaveOptions = new breeze.SaveOptions({
      resourceName: `saveanon?ts=${Date.now()}`,
    });
    const mgr: breeze.EntityManager = getEntityManager();

    let failed: boolean = false;
    let error: string = '';

    const saveResult = await mgr.saveChanges(entities, saveOptions)
      .catch((err) => {
        const entityErrors: any = err.entityErrors;
        console.log('Save Error');
        console.log(err);
        console.log('Save Entities');
        console.log(entities);
        console.log('Entity Errors');
        console.log(entityErrors);
        failed = true;
        const reportData = this.sentry.toView(
          `Repository (saveAnon): ${this.buildEntityErrorString(entityErrors)}. ${context ? `. Context: ${context}` : ''}`);
        const reportError = this.sentry.toView(
          'Repository errors (saveAnon)',
          err);
        error = err;
      });

    if (failed || !saveResult) {
      return Promise.reject(error);
    }

    // console.log(`Saved entities: ${saveResult.entities}`);

    return Promise.resolve(saveResult);

  }

  async saveAdmin(entities: breeze.Entity[] | undefined, context?: string):
    Promise<breeze.SaveResult> {

    // set up anti-forgery token if not already done
    // if (!this.identity.antiForgeryDone || this.identity.forceAntiForgeryUpdate) {
      // this creates headers for http fetch client and breeze adapter
    await this.identity.initialiseIdentityAsync();
    // }

    const adapter: any = breeze.config.getAdapterInstance('ajax');

    const saveOptions: breeze.SaveOptions = new breeze.SaveOptions(
      { resourceName: `saveadminchanges?ts=${Date.now()}` });
    const mgr: breeze.EntityManager = getEntityManager();

    let failed: boolean = false;
    let error: any = {};

    const saveResult = await mgr.saveChanges(entities, saveOptions)
      .catch((err) => {
        const entityErrors: any = err.entityErrors;
        console.log('Save Error');
        console.log(err);
        console.log('Save Entities');
        console.log(entities);
        console.log('Entity Errors');
        console.log(entityErrors);
        failed = true;

        const reportData = this.sentry.toView(
          `Repository (saveAdmin): ${this.buildEntityErrorString(entityErrors)}. ${context ? `\nContext: ${context}` : ''}`);

        const reportError = this.sentry.toView(
          'Repository errors (saveAdmin)',
          err);
        // error = err.entityErrors ? err.entityErrors : err;
        error = err;
        // 404 = controller method not allowed (i.e. user logged out or not admin
        // so reload to force login)
        // Or not found (should never happen)
        // 406 = token error (probably other tabs open so reload)
        if (err.status === 406 || err.status === 404) {
          window.location.reload();
        }
        error.status = 500;
      });

    if (failed || !saveResult) {
      return Promise.reject(error);
    }

    console.log(saveResult);
    console.log(`Saved entities: ${saveResult.entities}`);

    return Promise.resolve(saveResult);

  }

  async saveMember(entities: breeze.Entity[] | undefined, personId: string, context?: string):
    Promise<breeze.SaveResult> {

    // set up anti-forgery token if not already done
    // if (!this.identity.antiForgeryDone || this.identity.forceAntiForgeryUpdate) {
      // this creates headers for http fetch client and breeze adapter
    await this.identity.initialiseIdentityAsync();
    // }

    const adapter: any = breeze.config.getAdapterInstance('ajax');

    const saveOptions: breeze.SaveOptions = new breeze.SaveOptions(
      { resourceName: `savememberchanges/pId/${personId}?ts=${Date.now()}` });
    const mgr: breeze.EntityManager = getEntityManager();

    let failed: boolean = false;
    let error: any = {};
    console.log(entities);

    console.log(this.breezeManager.hasChanges());

    const saveResult = await mgr.saveChanges(entities, saveOptions)
      .catch((err) => {
        const entityErrors: any = err.entityErrors;
        console.log('Save Error');
        console.log(err);
        console.log('Save Entities');
        console.log(entities);
        console.log('Entity Errors');
        console.log(entityErrors);
        failed = true;
        // error = err.entityErrors ? err.entityErrors : err;
        error = err;
        // 404 = controller method not allowed (i.e. user logged out so reload to force login)
        // Or not found (should never happen)
        // 406 = token error (probably other tabs open so reload)
        if (err.status === 406 || err.status === 404) {
          window.location.reload();
        }
        error.status = 500;

        const reportData = this.sentry.toView(
          `Repository (saveMember): ${this.buildEntityErrorString(entityErrors)}. ${context ? `\nContext: ${context}` : ''}`);

        const reportError = this.sentry.toView(
          'Repository errors (saveMember)',
          err);
      });

    if (failed || !saveResult) {
      return Promise.reject(error);
    }

    console.log(saveResult);
    console.log(`Saved entities: ${saveResult.entities}`);

    return Promise.resolve(saveResult);

  }

  cancelChanges(): boolean {

    const mgr: breeze.EntityManager = getEntityManager();
    mgr.rejectChanges();
    return true;

  }

  generateGuid() {
    // time/MAC based
    // return uuid.v1();
    // random
    return uuid.v4();
  }

  flattenEntity(entityTypeName: string, entity: any): any {

    const flattenedEntity: any = {};

    // if (this.breezeManager) {
    const entityType: any =
      this.breezeManager.metadataStore.getEntityType(entityTypeName);

    entityType.dataProperties.forEach((property: any) => {
      flattenedEntity[property.name] = entity[property.name];
    });
    // }

    return flattenedEntity;

  }

}
